import { ReactElement, useState, useEffect, useCallback } from 'react';
import { MenuItem, SelectChangeEvent, Stack, Typography } from '@mui/material';

import {
  ToleranceTimePickerDurationUnit,
  ToleranceTimePickerState,
  ToleranceTimePickerProps,
} from './types';
import {
  ToleranceTimePickerContainer,
  TitleTypography,
  IncreaseButton,
  DecreaseButton,
  TimeFactorSelect,
} from './styles';

const MINUTES_IN_HOUR = 60;
const MINUTES_IN_DAY = 1440;
const STEP = 5;
const MINUTES_BEFORE_HOUR = MINUTES_IN_HOUR - STEP;

const FACTORS_OPTIONS: {
  value: ToleranceTimePickerDurationUnit;
  label: string;
}[] = [
  { value: 'minutes', label: 'minutos' },
  { value: 'hours', label: 'horas' },
];

const transformedMinutesToPickerState = (
  minutes: number,
): ToleranceTimePickerState => {
  const factor: ToleranceTimePickerDurationUnit =
    minutes < MINUTES_IN_HOUR ? 'minutes' : 'hours';

  const count = factor === 'minutes' ? minutes : minutes / MINUTES_IN_HOUR;

  return { factor, count, minutes };
};

const getMinutesStepByFactor = (
  factor: ToleranceTimePickerDurationUnit,
): number => {
  if (factor === 'hours') {
    return MINUTES_IN_HOUR;
  }

  return STEP;
};

const ToleraceTimePicker = ({
  value: minutes,
  onChange,
  title,
}: ToleranceTimePickerProps): ReactElement => {
  const [state, setState] = useState(transformedMinutesToPickerState(minutes));

  useEffect(() => {
    setState(transformedMinutesToPickerState(minutes));
  }, [minutes]);

  const handleIncrease = useCallback(() => {
    if (state.factor === 'hours' && state.minutes === MINUTES_IN_DAY) {
      return;
    }

    let newMinutes = state.minutes + getMinutesStepByFactor(state.factor);

    if (state.minutes === MINUTES_BEFORE_HOUR) {
      newMinutes = MINUTES_IN_HOUR;
    }

    const newState = transformedMinutesToPickerState(newMinutes);

    onChange(newState);
  }, [onChange, state.factor, state.minutes]);

  const handleDecrease = useCallback(() => {
    if (state.factor === 'minutes' && state.minutes === STEP) {
      return;
    }

    let newMinutes = state.minutes - getMinutesStepByFactor(state.factor);

    if (state.factor === 'hours' && state.minutes === MINUTES_IN_HOUR) {
      newMinutes = MINUTES_BEFORE_HOUR;
    }

    const newState = transformedMinutesToPickerState(newMinutes);

    onChange(newState);
  }, [onChange, state.factor, state.minutes]);

  const handleChangeFactor = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const newFactor = event.target.value as ToleranceTimePickerDurationUnit;

      let currentMinutes =
        newFactor === 'hours' ? (state.minutes *= MINUTES_IN_HOUR) : STEP;

      if (currentMinutes > MINUTES_IN_DAY) {
        currentMinutes = MINUTES_IN_HOUR;
      }

      const newState = transformedMinutesToPickerState(currentMinutes);

      onChange(newState);
    },
    [onChange, state],
  );

  return (
    <ToleranceTimePickerContainer>
      <TitleTypography>{title ?? ''}</TitleTypography>

      <Stack spacing={1} direction="row">
        <Stack sx={{ alignItems: 'center', ml: 1 }} direction="row">
          <Stack mr={1}>
            <IncreaseButton onClick={handleIncrease}>+</IncreaseButton>

            <DecreaseButton onClick={handleDecrease}>-</DecreaseButton>
          </Stack>

          <Typography width={20} textAlign="center">
            {state.count}
          </Typography>
        </Stack>

        <TimeFactorSelect
          fullWidth
          disableUnderline
          variant="standard"
          value={state.factor}
          onChange={handleChangeFactor}
        >
          {FACTORS_OPTIONS.map((option) => (
            <MenuItem
              key={`tolerance-time-picker-factor-${option.value}`}
              value={option.value}
            >
              {option.label}
            </MenuItem>
          ))}
        </TimeFactorSelect>
      </Stack>
    </ToleranceTimePickerContainer>
  );
};

export default ToleraceTimePicker;
